
import { useState, useEffect } from 'react';
import { checkBrowserSupport, getSupportMessage, initSpeechRecognition } from '../utils/browserSupport';

interface VoiceSupport {
  isSupported: boolean;
  hasPermission: boolean | null;
  permissionError: string | null;
  browserSupportMessage: string;
  requestPermission: () => Promise<boolean>;
  initializeRecognition: () => SpeechRecognition | null;
}

export const useVoiceSupport = (): VoiceSupport => {
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [browserSupportMessage, setBrowserSupportMessage] = useState<string>('');
  
  // Check browser support on mount
  const features = checkBrowserSupport();
  const isSupported = features.speechRecognition && features.mediaDevices;
  
  useEffect(() => {
    setBrowserSupportMessage(getSupportMessage(features));
  }, []);
    
  // Request microphone permission with enhanced error handling
  const requestPermission = async (): Promise<boolean> => {
    if (!isSupported) {
      setPermissionError('Speech recognition is not supported in your browser');
      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setHasPermission(true);
      setPermissionError(null);
      return true;
    } catch (error) {
      setHasPermission(false);
      let errorMessage = 'Failed to access microphone';
      
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          errorMessage = 'Microphone access denied. Please enable microphone permissions in your browser settings.';
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'No microphone detected. Please connect a microphone and try again.';
        } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
          errorMessage = 'Your microphone is being used by another application.';
        } else {
          errorMessage = `Failed to access microphone: ${error.message}`;
        }
      }
      
      setPermissionError(errorMessage);
      return false;
    }
  };

  // Initialize speech recognition with proper browser support
  const initializeRecognition = (): SpeechRecognition | null => {
    return initSpeechRecognition();
  };

  return {
    isSupported,
    hasPermission,
    permissionError,
    browserSupportMessage,
    requestPermission,
    initializeRecognition
  };
};
